import axios from "axios";
import { getToken, clearAuth } from "@/lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 30000 });

// Attach the JWT to every request
client.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// On 401, drop the token and bounce to the login screen
client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      clearAuth();
      if (window.location.pathname !== "/login") window.location.assign("/login");
    }
    return Promise.reject(err);
  }
);

export const get = (path, params) => client.get(path, { params }).then((r) => r.data);
export const post = (path, body) => client.post(path, body).then((r) => r.data);

export const endpoints = {
  login: (username, password) => post("/auth/login", { username, password }),
  me: () => get("/auth/me"),
  health: () => get("/health"),
  ribbons: () => get("/ribbons"),
  enterprise: () => get("/enterprise"),
  processMimic: () => get("/process-mimic"),
  economics: () => get("/economics"),
  twins: () => get("/twins"),
  twin: (id) => get(`/twins/${id}`),
  sustainability: () => get("/sustainability"),
  sankey: (metric) => get("/sankey", { metric }),
  anomalies: () => get("/anomalies"),
  trend: () => get("/trend"),
  ontology: () => get("/ontology"),
  simConfig: () => get("/simulation/config"),
  runScenario: (body) => post("/simulation/scenario", body),
  injectAnomaly: (anomaly_type, severity) =>
    post("/simulation/anomaly/inject", { anomaly_type, severity }),
  clearAnomaly: (anomaly_type) => post("/simulation/anomaly/clear", { anomaly_type }),
  generateBrief: () => post("/ai/brief", {}),
  cachedBrief: () => get("/ai/brief"),
  // P1: connectors / mode
  mode: () => get("/mode"),
  setMode: (mode) => post("/mode", { mode }),
  shadow: () => get("/shadow"),
  // P1: history + replay
  history: (hours, metric) => get("/history", { hours, metric }),
  replay: (hours) => get("/history/replay", { hours }),
  // P1: assets + training
  assets: () => get("/assets"),
  // P2: product economics
  products: () => get("/products"),
  trainingScenarios: () => get("/training/scenarios"),
  trainingSubmit: (name, answers) => post("/training/submit", { name, answers }),
  trainingLeaderboard: () => get("/training/leaderboard"),
  // P2+: DB inspector
  dbCollections: () => get("/db/collections"),
  dbCollection: (name, limit) => get(`/db/${name}`, { limit }),
};

// Streaming chat via fetch + SSE parsing
export async function streamChat(sessionId, message, onDelta, onMeta) {
  const token = getToken();
  const res = await fetch(`${API}/ai/chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ session_id: sessionId, message }),
  });
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop();
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith("data:")) continue;
      try {
        const payload = JSON.parse(line.slice(5).trim());
        if (payload.session_id && onMeta) onMeta(payload.session_id);
        if (payload.delta) onDelta(payload.delta);
        if (payload.error) onDelta(`\n\n_Error: ${payload.error}_`);
      } catch (e) {
        /* ignore */
      }
    }
  }
}
